/**
 * A vendor rental's card — `docs/visit-page.md` §10.2, §27.4 (Board 5 A's two
 * cards, Board 6 A *To book*, Board 18 I *Mark picked up*).
 *
 * The truck tile, the description, the day chips and the date, the state chip;
 * the vendor block with **Navigate**; the steps (dots filled when done); the
 * internal money; the foot — the bill note and the agreement, or the crew
 * note and **Change · Cancel booking**. A rental *to book* is the dashed card
 * of Board 6 A with **Book it**.
 *
 * While the crew app has no rental stops (`crew.rental_stops` off — the page
 * reads `capabilities` and nothing else, §27.1) the next stop carries **Mark
 * picked up**, then **Mark returned**, and one line under the steps says why.
 */
import type { IsoMoment, RentalCard as RentalCardData, RentalStep, VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { mapsHref } from '../lib/maps.js'
import { COMMON, RENTALS } from './copy.js'
import { dayRange, dayText, money, timeIn } from './format.js'
import { VisitGlyph } from './glyphs.js'
import { CardButton, DayChip, InternalLine, KindTile, RentalChip } from './equipment-parts.js'

export interface RentalCardProps {
  card: RentalCardData
  read: VisitRead
  phone: boolean
  /** **Book it** (to book) — opens the Rent sheet on this rental. */
  onBook: () => void
  /** **Change** — the Rent sheet on this rental. */
  onChange: () => void
  /** **Cancel booking** — asks why. */
  onCancel: () => void
  /** **Mark picked up** / **Mark returned** (Board 18 I). */
  onMark: (stop: 'pick_up' | 'return') => void
  /** A stop is being marked — the button waits. */
  busy?: boolean
}

/** *Wed Oct 21* for one day, *Wed Oct 21 – Thu Oct 22* for several. */
function daysWhen(card: RentalCardData): string {
  const first = card.days[0]
  const last = card.days[card.days.length - 1]
  if (!first || !last) return ''
  return first.index === last.index ? dayText(first.date, 'date') : dayRange(first.date, last.date, 'long')
}

/** The vendor's street and town, and its phone — *4710 Industrial Rd, Harrisburg · (717) 555-0122*. */
function vendorLine(card: RentalCardData): string | null {
  const vendor = card.vendor
  if (!vendor) return null
  const place = vendor.line1 && vendor.city
    ? RENTALS.address(vendor.line1, vendor.city)
    : vendor.line1 ?? vendor.city
  if (place && vendor.phone) return COMMON.dot(place, vendor.phone)
  return place ?? vendor.phone
}

/** Who a stop names: the person, else (a vendor that delivers) the vendor's short name. */
function stopName(card: RentalCardData, step: Extract<RentalStep, { kind: 'pick_up' | 'return' }>): string {
  return step.person?.name ?? card.vendor?.shortName ?? card.description
}

export function RentalCard({ card, read, phone, onBook, onChange, onCancel, onMark, busy = false }: RentalCardProps) {
  const zone = read.timeZone
  const at = (iso: IsoMoment | null, style: 'date' | 'dayAmpm') => (iso ? timeIn(zone, iso, style) : null)
  const when = daysWhen(card)

  if (card.state === 'to_book') {
    const first = card.days[0]
    return (
      <article
        data-as-drawn="vp-06 A"
        className="flex min-w-0 flex-col gap-2.5 rounded-record border-[1.5px] border-dashed border-line-strong bg-surface px-4 py-3.5"
      >
        <header className="flex items-center gap-2.5">
          <KindTile kind="rental" glyph="truck" size={34} />
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-[14px]">{card.description}</b>
            <div className="flex items-center gap-1.5">
              <span className="inline-flex gap-[3px]">{card.days.map((day) => <DayChip key={day.index} n={day.index} />)}</span>
              <span className="truncate text-[11.5px] text-text-muted">
                {card.quoteReference ? RENTALS.toBook.when(when, card.quoteReference) : when}
              </span>
            </div>
          </div>
          <span className="flex-1" />
          <RentalChip state={card.state} />
        </header>
        {first && (
          <p className="m-0 text-[12.5px] leading-[1.45] text-text-body">
            {RENTALS.toBook.line(dayText(first.date, 'weekday'))}
          </p>
        )}
        {card.internal && (
          <InternalLine
            text={RENTALS.rateEstimated(money(card.internal.rate), String(card.internal.days))}
            amount={money(card.internal.amount)}
          />
        )}
        {card.can.book && read.can.edit && (
          <div className="flex items-center gap-2">
            <span className="flex-1" />
            <button
              type="button"
              onClick={onBook}
              className={cn('inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-tile border',
                'border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark',
                phone ? 'h-11' : 'h-8')}
            >
              <VisitGlyph name="check" size={14.5} stroke={2} />
              {RENTALS.toBook.bookIt}
            </button>
          </div>
        )}
      </article>
    )
  }

  /* §27.4: the office marks the stops only while the crew app cannot. */
  const standIn = !read.capabilities['crew.rental_stops'] && read.can.edit && card.nextStop !== null
  const line = vendorLine(card)
  const navigate = card.vendor ? mapsHref([card.vendor.line1, card.vendor.city]) ?? undefined : undefined
  const footText = card.bill === 'not_in_yet' && card.vendor
    ? RENTALS.billNotIn(card.vendor.shortName)
    : card.crewNote ? RENTALS.note(card.crewNote) : null
  const change = card.can.change && read.can.edit
  const cancel = card.can.cancel && read.can.edit

  return (
    <article
      {...(standIn ? { 'data-as-drawn': 'vp-18 I' } : {})}
      className="flex min-w-0 flex-col gap-[11px] rounded-record border border-line bg-surface px-4 py-3.5"
    >
      <header className="flex items-center gap-2.5">
        <KindTile kind="rental" glyph="truck" size={34} />
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[14px]">{card.description}</b>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex gap-[3px]">{card.days.map((day) => <DayChip key={day.index} n={day.index} />)}</span>
            <span className="truncate text-[11.5px] text-text-muted">{when}</span>
          </div>
        </div>
        <span className="flex-1" />
        <RentalChip state={card.state} />
      </header>

      {card.vendor && (
        <div className="flex gap-2.5 rounded-card border border-line-faint bg-surface-alt px-3 py-2.5">
          <KindTile kind="rental" glyph="vendor" size={30} />
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-[12.5px]">{card.vendor.name}</b>
            {line && <span className="text-[11.5px] text-text-muted">{line}</span>}
            {card.vendor.hours && <span className="text-[11.5px] text-text-muted">{card.vendor.hours}</span>}
          </div>
          {navigate && (phone
            ? (
              /* A phone keeps the address on its lines: the button is the 44px square with its glyph. */
              <a href={navigate} target="_blank" rel="noopener noreferrer" aria-label={RENTALS.navigate}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-line bg-surface text-text">
                <VisitGlyph name="navigate" size={18} stroke={2} />
              </a>
            )
            : <CardButton height={28} phone={false} glyph="navigate" href={navigate}>{RENTALS.navigate}</CardButton>)}
        </div>
      )}

      <div>
        {card.steps.map((step, index) => {
          const last = index === card.steps.length - 1
          let title: string
          let meta: string | null
          if (step.kind === 'booked') {
            title = step.confirmation ? RENTALS.stops.booked(step.confirmation) : RENTALS.state.booked
            const day = at(step.at, 'date')
            meta = day && step.by ? COMMON.dot(day, step.by.name) : day ?? step.by?.name ?? null
          } else if (step.kind === 'pick_up') {
            title = step.done ? RENTALS.stops.pickedUp(stopName(card, step)) : RENTALS.stops.pickUp(stopName(card, step))
            meta = at(step.at, 'dayAmpm')
          } else {
            title = step.done ? RENTALS.stops.returned(stopName(card, step)) : RENTALS.stops.returnBy(stopName(card, step))
            meta = at(step.at, 'dayAmpm')
          }
          const marks = standIn && !step.done && step.kind === card.nextStop
          return (
            <div key={step.kind} className="flex gap-2.5">
              <div className="flex flex-col items-center pt-1">
                <i
                  className={cn('block h-2.5 w-2.5 rounded-full',
                    step.done ? 'bg-accent' : 'border-2 border-line-strong bg-surface')}
                />
                {/* Board 18 I draws the stand-in's steps without the line between them (Board 5 draws it). */}
                {!last && !standIn && <i className="mt-0.5 block min-h-3 w-0.5 flex-1 bg-line" />}
              </div>
              <div className="min-w-0 flex-1 pb-2">
                <span className="text-[12.5px] font-semibold text-text">{title}</span>
                {meta && <span className="ml-1.5 text-[11.5px] text-text-faint">{meta}</span>}
              </div>
              {marks && (
                <span className="self-start">
                  <CardButton height={28} phone={phone} glyph="check" onClick={busy ? undefined : () => onMark(step.kind as 'pick_up' | 'return')}>
                    {step.kind === 'pick_up' ? RENTALS.standIn.markPickedUp : RENTALS.standIn.markReturned}
                  </CardButton>
                </span>
              )}
            </div>
          )
        })}
      </div>

      {standIn && (
        <p className="m-0 rounded-control border border-line-faint bg-surface-alt px-2.5 py-2 text-[11.5px] leading-[1.4] text-text-body">
          {RENTALS.standIn.line}
        </p>
      )}

      {card.internal && (
        <InternalLine
          text={RENTALS.rate(money(card.internal.rate), String(card.internal.days))}
          amount={money(card.internal.amount)}
        />
      )}

      {(footText || card.agreement || change || cancel) && (
        <div className={cn('flex gap-2.5', phone ? 'flex-col items-stretch' : 'items-center')}>
          <span className="min-w-0 flex-1 text-[11.5px] leading-[1.4] text-text-muted">{footText}</span>
          {card.agreement && (
            <div className="flex items-center gap-2">
              <CardButton height={30} phone={phone} glyph="doc" href={card.agreement.url}>{card.agreement.name}</CardButton>
            </div>
          )}
          {(change || cancel) && (
            <div className="flex items-center gap-1.5">
              {change && <CardButton height={30} phone={phone} glyph="pencil" onClick={onChange}>{RENTALS.change}</CardButton>}
              {cancel && <CardButton height={30} phone={phone} onClick={onCancel}>{RENTALS.cancelBooking}</CardButton>}
            </div>
          )}
        </div>
      )}
    </article>
  )
}
